import toast from 'react-hot-toast';
import { client } from '@/services/api/axios.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DeviceTypeCodes } from '@/features/devices/schemas/devices.schemas.ts';

type OverviewType = 'production' | 'storage';

type DeviceFieldsConfig = Partial<Record<DeviceTypeCodes, number[]>>;

type UpdatePlantOverviewConfigRequest = {
  plantId: string;
  type: OverviewType;
  config: DeviceFieldsConfig;
};

type UpdatePlantOverviewConfigResponse = {
  body: {
    type: OverviewType;
    config: DeviceFieldsConfig;
  };
};

type UseUpdatePlantOverviewConfigOptions = {
  onSuccess?: (data: UpdatePlantOverviewConfigResponse['body']) => void;
  onError?: (error: Error) => void;
  showToast?: boolean;
};

export const useUpdatePlantOverviewConfig = ({
  onSuccess,
  onError,
  showToast = true,
}: UseUpdatePlantOverviewConfigOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ plantId, type, config }: UpdatePlantOverviewConfigRequest) => {
      const { data } = await client.post<UpdatePlantOverviewConfigResponse>(
        `/plants/${plantId}/overview/config`,
        { type, config }
      );

      return data.body;
    },
    onSuccess: (data) => {
      if (showToast) {
        toast.success('Device fields configuration updated successfully');
      }
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      if (showToast) {
        toast.error('Failed to update device fields configuration');
      }
      onError?.(error);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['plants', variables.plantId, 'devices', 'overview'],
      });
    },
  });
};
